/**
 * User Activity Page
 * Shows detailed logs of which user added which contacts on which date
 * Only accessible by superadmin
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../utils/api';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import CountrySelect from '../components/CountrySelect';
import UserSearchSelect from '../components/UserSearchSelect';
import PaymentModal from '../components/PaymentModal';
import UserPrivilegeMatrix from '../components/UserPrivilegeMatrix';
import { getAllISOCodes } from 'iso-country-currency';

const INSTITUTION_TYPES = [
  'Universities',
  'Schools',
  'Government Departments',
  'Ministries',
  'Embassies & High Commissions',
  'NGOs & Foundations',
  'Companies'
];

const UserActivity = () => {
  const { user } = useSelector((state) => state.auth);

  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState({
    totalContacts: 0,
    uniqueUsers: 0,
    contactsByDate: []
  });
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [selectedUser, setSelectedUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [institutionType, setInstitutionType] = useState('');
  const [country, setCountry] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [localSearch, setLocalSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(''); // For week-based filtering in user card

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedWeekForPayment, setSelectedWeekForPayment] = useState(null);

  // Privilege modal state
  const [showPrivilegeModal, setShowPrivilegeModal] = useState(false);

  // Fetch activity logs
  const fetchActivityLogs = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      if (selectedUser) params.append('userId', selectedUser);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (institutionType) params.append('institutionType', institutionType);
      if (country) params.append('country', country);
      if (searchQuery) params.append('search', searchQuery);

      const response = await api.get(`/user-stats/activity-logs?${params.toString()}`);

      if (response.data.success) {
        setActivities(response.data.data.activities);
        setSummary(response.data.data.summary);
        setPagination(response.data.data.pagination);

        // Use backend-provided userSummary instead of client calculation
        if (response.data.data.userSummary) {
          setSelectedUserDetails(response.data.data.userSummary);
        } else {
          setSelectedUserDetails(null);
        }
      }
    } catch (err) {
      console.error('Fetch activity logs error:', err);
      setError(err.response?.data?.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  // Removed: calculateUserDetails - now handled by backend

  // Initial load
  useEffect(() => {
    fetchActivityLogs(1);
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchQuery) {
        setSearchQuery(localSearch);
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Fetch when filters or search change
  useEffect(() => {
    if (searchQuery !== undefined) {
      fetchActivityLogs(1);
    }
  }, [selectedUser, dateFrom, dateTo, institutionType, country, searchQuery]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchActivityLogs(newPage);
  };

  const handleClearFilters = () => {
    setSelectedUser('');
    setDateFrom('');
    setDateTo('');
    setInstitutionType('');
    setCountry('');
    setSearchQuery('');
    setLocalSearch('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Payment modal handlers
  const handleWeekClick = (week) => {
    setSelectedWeekForPayment(week);
    setShowPaymentModal(true);
  };

  const handlePaymentSave = (paymentData) => {
    if (!selectedUserDetails) return;

    // Update the local state immediately to avoid refetching
    const updatedUserDetails = { ...selectedUserDetails };

    if (paymentData === null) {
      // Payment was deleted - remove payment from the week
      updatedUserDetails.weeklyData = updatedUserDetails.weeklyData.map(week => {
        if (week.week === selectedWeekForPayment?.week) {
          const { payment, ...weekWithoutPayment } = week;
          return weekWithoutPayment;
        }
        return week;
      });

      // Recalculate totals
      const remainingPayments = updatedUserDetails.weeklyData
        .map(w => w.payment)
        .filter(p => p && p.paymentStatus === 'Paid');

      const newTotalSpent = remainingPayments
        .reduce((sum, p) => sum + parseFloat(p.total), 0);

      updatedUserDetails.totalSpent = newTotalSpent.toFixed(2);
      updatedUserDetails.totalPayments = updatedUserDetails.weeklyData.filter(w => w.payment).length;
      updatedUserDetails.paidPayments = remainingPayments.length;
    } else {
      // Payment was saved/updated - update the week with new payment data
      updatedUserDetails.weeklyData = updatedUserDetails.weeklyData.map(week => {
        if (week.week === selectedWeekForPayment?.week) {
          return {
            ...week,
            payment: paymentData
          };
        }
        return week;
      });

      // Recalculate totals
      const allPayments = updatedUserDetails.weeklyData
        .map(w => w.payment)
        .filter(p => p);

      const paidPayments = allPayments.filter(p => p.paymentStatus === 'Paid');
      const newTotalSpent = paidPayments.reduce((sum, p) => sum + parseFloat(p.total), 0);

      updatedUserDetails.totalSpent = newTotalSpent.toFixed(2);
      updatedUserDetails.totalPayments = allPayments.length;
      updatedUserDetails.paidPayments = paidPayments.length;
    }

    // Update the state
    setSelectedUserDetails(updatedUserDetails);
    setShowPaymentModal(false);
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      superadmin: 'bg-red-100 text-red-800',
      global_admin_hq: 'bg-purple-100 text-purple-800',
      country_director: 'bg-blue-100 text-blue-800',
      institutional_relations_officer: 'bg-green-100 text-green-800',
      legal_compliance_team: 'bg-yellow-100 text-yellow-800',
      read_only_auditor: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (!user || user.role !== 'superadmin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Activity Logs</h1>
            <p className="text-sm text-gray-600 mt-1">
              Track which users added which contacts and when
            </p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-blue-600 font-semibold uppercase tracking-wider">Total Contacts</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900 mt-1">{summary.totalContacts}</p>
              </div>
              <div className="bg-white bg-opacity-50 p-2 rounded-lg">
                <BuildingOfficeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-green-600 font-semibold uppercase tracking-wider">Active Users</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900 mt-1">{summary.uniqueUsers}</p>
              </div>
              <div className="bg-white bg-opacity-50 p-2 rounded-lg">
                <UserIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold uppercase tracking-wider">Current Page</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{pagination.page} / {pagination.pages}</p>
              </div>
              <div className="bg-white bg-opacity-50 p-2 rounded-lg">
                <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected User Details Card */}
      {selectedUserDetails && (
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-lg border-2 border-blue-100 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between mb-6 gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl h-16 w-16 flex items-center justify-center font-bold text-2xl shadow-lg transform rotate-3">
                {selectedUserDetails.user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedUserDetails.user?.name}</h3>
                <p className="text-sm text-gray-600 font-medium">{selectedUserDetails.user?.email}</p>
                <div className="mt-2 flex flex-wrap justify-center sm:justify-start gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getRoleBadgeColor(selectedUserDetails.user?.role)}`}>
                    {selectedUserDetails.user?.role?.replace(/_/g, ' ')}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${selectedUserDetails.user?.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {selectedUserDetails.user?.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {selectedUserDetails.user?.sendEmailIndividually && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm bg-indigo-100 text-indigo-800" title="Can send emails using individual sender credentials">
                      <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Individual Send
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto justify-center">
              {/* User Privileges Button */}
              <button
                onClick={() => setShowPrivilegeModal(true)}
                className="flex-1 sm:flex-none flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-bold"
                title="View User Privileges"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Privileges
              </button>

              {/* Close Button */}
              <button
                onClick={() => {
                  setSelectedUser('');
                  setSelectedUserDetails(null);
                }}
                className="bg-gray-100 text-gray-500 hover:text-gray-700 p-2.5 rounded-xl hover:bg-gray-200 transition-colors shadow-sm"
                title="Close user details"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-blue-50">
              <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Total Contacts</p>
              <p className="text-xl sm:text-2xl font-black text-blue-600">{selectedUserDetails.totalContacts || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-50">
              <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">First Contact</p>
              <p className="text-sm sm:text-base font-bold text-gray-900 truncate">
                {selectedUserDetails.firstContactDate ? new Date(selectedUserDetails.firstContactDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                }) : 'N/A'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-50">
              <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Latest Contact</p>
              <p className="text-sm sm:text-base font-bold text-gray-900 truncate">
                {selectedUserDetails.lastContactDate ? new Date(selectedUserDetails.lastContactDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                }) : 'N/A'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-50">
              <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Active Period</p>
              <p className="text-sm sm:text-base font-bold text-gray-900">
                {selectedUserDetails.activePeriodDays || 0} {selectedUserDetails.activePeriodDays === 1 ? 'day' : 'days'}
              </p>
            </div>
          </div>

          {/* User Profile Information - Enhanced Design */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-sm border border-gray-100 mb-4">
            {/* User Name and Email - Prominent */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {selectedUserDetails.user?.name}
              </h3>
              <a href={`mailto:${selectedUserDetails.user?.email}`} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center">
                <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {selectedUserDetails.user?.email}
              </a>
            </div>

            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Profile Details</h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Role */}
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="bg-blue-100 rounded-lg p-2 mr-3">
                    <UserIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium mb-1">Role</p>
                    <p className="text-sm font-bold text-gray-900 capitalize">
                      {selectedUserDetails.user?.role?.replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className={`rounded-lg p-2 mr-3 ${selectedUserDetails.user?.isActive ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                    <svg className={`h-5 w-5 ${selectedUserDetails.user?.isActive ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium mb-1">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${selectedUserDetails.user?.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {selectedUserDetails.user?.isActive ? '● Active' : '● Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Countries */}
              {selectedUserDetails.user?.countries && selectedUserDetails.user.countries.length > 0 && (
                <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:shadow-md transition-shadow md:col-span-2">
                  <div className="flex items-start">
                    <div className="bg-blue-100 rounded-lg p-2 mr-3">
                      <GlobeAltIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 font-medium mb-2">Countries</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedUserDetails.user.countries.map((country, idx) => (
                          <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            🌍 {country}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Regions */}
              {selectedUserDetails.user?.regions && selectedUserDetails.user.regions.length > 0 && (
                <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:shadow-md transition-shadow md:col-span-2">
                  <div className="flex items-start">
                    <div className="bg-blue-100 rounded-lg p-2 mr-3">
                      <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 font-medium mb-2">Regions</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedUserDetails.user.regions.map((region, idx) => (
                          <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            📍 {region}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Institution Types */}
              {selectedUserDetails.user?.institutionTypes && selectedUserDetails.user.institutionTypes.length > 0 && (
                <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:shadow-md transition-shadow md:col-span-2">
                  <div className="flex items-start">
                    <div className="bg-amber-100 rounded-lg p-2 mr-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 font-medium mb-2">Access to Institution Types</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedUserDetails.user.institutionTypes.map((type, idx) => (
                          <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            🏢 {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Joined Date */}
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="bg-teal-100 rounded-lg p-2 mr-3">
                    <CalendarIcon className="h-5 w-5 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium mb-1">Joined</p>
                    <p className="text-sm font-bold text-gray-900">
                      {new Date(selectedUserDetails.user?.createdAt).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Age */}
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="bg-pink-100 rounded-lg p-2 mr-3">
                    <ClockIcon className="h-5 w-5 text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 font-medium mb-1">Account Age</p>
                    <p className="text-sm font-bold text-gray-900">
                      {Math.floor((new Date() - new Date(selectedUserDetails.user?.createdAt)) / (1000 * 60 * 60 * 24))} days
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Activity Section */}
          {selectedUserDetails.weeklyData && selectedUserDetails.weeklyData.length > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-2 text-blue-600" />
                  Weekly Activity
                </h4>
                <p className="text-xs text-gray-500">Click on a week to manage payment</p>
              </div>

              {/* Weekly Cards Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
                {selectedUserDetails.weeklyData.map((week, idx) => (
                  <div
                    key={week.week}
                    onClick={() => handleWeekClick(week)}
                    className="bg-white border border-blue-100 rounded-xl p-3 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all duration-200 group flex flex-col h-full"
                  >
                    {/* Week Header */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-black text-blue-600 uppercase">Week {idx + 1}</span>
                      <CurrencyDollarIcon className="h-4 w-4 text-blue-400 group-hover:text-blue-600 transition-colors" />
                    </div>

                    {/* Contact Count */}
                    <div className="mb-2">
                      <p className="text-lg font-bold text-gray-900">{week.count}</p>
                      <p className="text-[10px] text-gray-500 font-bold uppercase">contacts</p>
                    </div>

                    {/* Date Range */}
                    <p className="text-[10px] text-gray-400 font-medium mb-3">
                      {new Date(week.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {' - '}
                      {new Date(week.weekEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>

                    {/* Payment Info */}
                    {week.payment ? (
                      <div className="border-t border-blue-50 pt-2 mt-auto">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] sm:text-xs font-bold text-gray-700">
                            {week.payment.currency} {parseFloat(week.payment.total).toFixed(2)}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] sm:text-[10px] font-black uppercase ${week.payment.paymentStatus === 'Paid'
                            ? 'bg-green-100 text-green-800'
                            : week.payment.paymentStatus === 'Pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                            }`}>
                            {week.payment.paymentStatus}
                          </span>
                        </div>
                        {week.payment.ratePerContact && (
                          <div className="text-[9px] text-gray-400 font-medium italic">
                            {week.payment.currency} {parseFloat(week.payment.ratePerContact).toFixed(2)}/c
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="border-t border-blue-100 pt-2 mt-auto">
                        <div className="flex items-center justify-center py-1.5 bg-blue-50 rounded-lg text-[10px] sm:text-xs text-blue-600 font-bold hover:bg-blue-100 transition-colors border border-blue-100 uppercase tracking-tighter">
                          <CurrencyDollarIcon className="h-3 w-3 mr-1" />
                          Add Payment
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Selected Week Details */}
              {selectedWeek && (
                (() => {
                  const weekData = selectedUserDetails.weeklyData.find(w => w.week === selectedWeek);
                  if (!weekData) return null;

                  return (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-semibold text-blue-900">
                          Week Details - {new Date(weekData.weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to {new Date(weekData.weekEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </h5>
                        <button
                          onClick={() => setSelectedWeek('')}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                        >
                          Close
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-blue-600 font-medium mb-1">Total Contacts</p>
                          <p className="text-2xl font-bold text-blue-900">{weekData.count}</p>
                        </div>

                        {weekData.payment && (
                          <div>
                            <p className="text-xs text-blue-600 font-medium mb-1">Payment Amount</p>
                            <p className="text-2xl font-bold text-green-600">
                              {weekData.payment.currency} {parseFloat(weekData.payment.total).toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>

                      {Object.keys(weekData.institutionTypes).length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-xs font-semibold text-blue-900 mb-2">Contacts by Type</h6>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(weekData.institutionTypes)
                              .sort(([, a], [, b]) => b - a)
                              .map(([type, count]) => (
                                <div key={type} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border border-blue-100">
                                  <span className="text-gray-700">{type}</span>
                                  <span className="font-semibold text-blue-600">{count}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {/* Total Spend Section */}
          {selectedUserDetails && (selectedUserDetails.totalSpent > 0 || selectedUserDetails.totalPayments > 0) && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 rounded-lg p-2 mr-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">Total Spend on This User</h4>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-green-100">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Total Paid</p>
                  <p className="text-xl sm:text-2xl font-black text-green-600">
                    ${parseFloat(selectedUserDetails.totalSpent || 0).toFixed(2)}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-blue-50">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Total Payments</p>
                  <p className="text-xl sm:text-2xl font-black text-blue-600">
                    {selectedUserDetails.totalPayments || 0}
                  </p>
                  <p className="text-[10px] text-gray-400 font-medium">
                    ({selectedUserDetails.paidPayments || 0} paid)
                  </p>
                </div>

                <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-blue-50 col-span-2 sm:col-span-1">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Avg. / Week</p>
                  <p className="text-xl sm:text-2xl font-black text-blue-600">
                    ${selectedUserDetails.totalPayments > 0
                      ? (parseFloat(selectedUserDetails.totalSpent || 0) / selectedUserDetails.totalPayments).toFixed(2)
                      : '0.00'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder="Search by organization, contact person, or email..."
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">User:</label>
              <UserSearchSelect
                value={selectedUser}
                onChange={setSelectedUser}
                placeholder="All Users"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">From Date:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">To Date:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Institution Type:</label>
              <select
                value={institutionType}
                onChange={(e) => setInstitutionType(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
              >
                <option value="">All Types</option>
                {INSTITUTION_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Country:</label>
              <CountrySelect
                value={country}
                onChange={setCountry}
                placeholder="All Countries"
                isClearable={true}
                className="text-xs"
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Activity Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">{error}</div>
        ) : activities.length === 0 ? (
          <div className="p-12 text-center">
            <BuildingOfficeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No activity found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institution Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activities.map((activity) => (
                    <tr key={activity.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          {formatDate(activity.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{activity.creator?.name}</div>
                          <div className="text-xs text-gray-500">{activity.creator?.email}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(activity.creator?.role)}`}>
                              {activity.creator?.role?.replace(/_/g, ' ')}
                            </span>
                            {activity.creator?.sendEmailIndividually && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" title="Individual Send">
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {activity.primaryName || activity.institutionName || activity.name || 'N/A'}
                        </div>
                        {activity.contactIndividual && (
                          <div className="text-xs text-gray-500">{activity.contactIndividual}</div>
                        )}
                        {activity.email && (
                          <div className="text-xs text-blue-600">{activity.email}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {activity.institutionType || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex items-center">
                          <GlobeAltIcon className="h-4 w-4 text-gray-400 mr-1" />
                          <div>
                            <div>{activity.country || 'N/A'}</div>
                            {activity.city && (
                              <div className="text-xs text-gray-500">{activity.city}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {activity.priority && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${activity.priority === 'High' ? 'bg-red-100 text-red-800' :
                            activity.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                            {activity.priority}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} activities
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedWeekForPayment && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          weekData={selectedWeekForPayment}
          userId={selectedUserDetails?.user?.id}
          onSave={handlePaymentSave}
        />
      )}

      {/* User Privilege Modal */}
      {showPrivilegeModal && selectedUserDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowPrivilegeModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white bg-opacity-20 rounded-full p-2">
                      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">User Privileges Matrix</h3>
                      <p className="text-blue-100 text-sm">
                        {selectedUserDetails.user?.name} - {selectedUserDetails.user?.role?.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPrivilegeModal(false)}
                    className="text-white hover:text-blue-200 transition-colors p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="max-h-[70vh] overflow-y-auto">
                <UserPrivilegeMatrix user={selectedUserDetails.user} />
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowPrivilegeModal(false)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActivity;
